import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, IconButton, useTheme, TextField, Dialog, DialogActions, DialogContent, DialogTitle, Checkbox, FormControlLabel, useMediaQuery, DialogContentText } from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { tokens } from '../../../theme';
import { addRole, deleteRole, getRoles, editRole } from '../../../redux/slice/roles.slice';
import Header from "../../../components/Header";
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';

const RoleDataGrid = ({ data, columns }) => {
    if (!data || !Array.isArray(data)) {
        return <div>No data availble...</div>;
    }
    return (
        <DataGrid
            rows={data}
            columns={columns}
            components={{ Toolbar: GridToolbar }}
            pageSize={5}
            rowsPerPageOptions={[5, 10]}
            getRowId={(row) => row._id}
            density="compact"
            autoHeight
            sx={{
                "@media print": {
                    "& .MuiDataGrid-toolbarContainer": {
                        display: "none !important"
                    },
                    "& .MuiDataGrid-root": {
                        color: "black !important",
                    },
                    "& .MuiDataGrid-cell": {
                        color: "black !important",
                    },
                    "& .MuiDataGrid-columnHeaders": {
                        color: "black !important",
                    },
                    "& .MuiDataGrid-footerContainer": {
                        color: "black !important",
                    },
                    "& .css-7ms3qr-MuiTablePagination-displayedRows": {
                        color: "black !important",
                    },
                },
            }}
        />
    );
};

const Role = () => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const colors = tokens(theme.palette.mode);
    const [open, setOpen] = useState(false);
    const [update, setUpdate] = useState(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [deleteRoleData, setDeleteRoleData] = useState(null);
    const [openView, setOpenView] = React.useState(false);
    const [viewData, setViewData] = useState(null);
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
    const roles = useSelector(state => state.roles.roles);
    const SideBar = useSelector((state) => state.sidebar.sidebar);
    const isMobile = useMediaQuery('(max-width:500px)');


    useEffect(() => {
        dispatch(getRoles());
    }, [dispatch]);

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        formik.resetForm();
        setUpdate(null);
    };

    const handleDelete = (id) => {
        setDeleteRoleData(id);
        setDeleteConfirmOpen(true);
    };

    const confirmDelete = () => {
        if (deleteRoleData) {
            dispatch(deleteRole(deleteRoleData));
            setDeleteConfirmOpen(false);
            setDeleteRoleData(null);
        }
    }
    const cancelDelete = () => {
        setDeleteConfirmOpen(false);
        setDeleteRoleData(null);
    }

    const handleEdit = (data) => {
        formik.setValues(data);
        setOpen(true);
        setUpdate(data._id);
    };

    const handleClickOpenView = () => {
        setOpenView(true);
    };

    const handleCloseView = () => {
        setOpenView(false);
    };

    const handleView = (data) => {
        handleClickOpenView();
        setViewData(data)
    }

    const columns = useMemo(() => [
        { field: 'roleName', headerName: 'Name', width: 200 },
        { field: 'description', headerName: 'Description', width: 450 },
        {
            field: 'permissions',
            headerName: 'Permissions',
            width: 600,
            renderCell: (params) => (
                <div style={{
                    width: '100%',
                    padding: '8px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                }}>
                    {params.row.permissions.join(' , ')}
                </div>
            )
        },
        {
            field: 'Action',
            headerName: 'Action',
            width: 130,
            renderCell: (params) => (
                <>
                    <IconButton
                        aria-label="view"
                        onClick={() => handleView(params.row)}
                    >
                        <RemoveRedEyeIcon />
                    </IconButton>
                    <IconButton aria-label="edit" onClick={() => handleEdit(params.row)}>
                        <EditIcon />
                    </IconButton>
                    <IconButton aria-label="delete" onClick={() => handleDelete(params.row._id)}>
                        <DeleteIcon />
                    </IconButton>
                </>
            )
        }
    ], []);

    const roleSchema = yup.object({
        roleName: yup.string().required("Please enter role name"),
        description: yup.string().required("Please enter role description"),
        permissions: yup.array().of(yup.string()),
    });

    const formik = useFormik({
        initialValues: {
            roleName: '',
            description: '',
            permissions: []
        },
        validationSchema: roleSchema,
        onSubmit: (values, { resetForm }) => {
            console.log(values);
            if (update) {
                dispatch(editRole({
                    _id: update,
                    roleName: values.roleName,
                    description: values.description,
                    permissions: values.permissions
                }));
            } else {
                console.log(values);
                dispatch(addRole(values));
            }
            resetForm();
            handleClose();
        }
    });

    const { handleSubmit, handleChange, handleBlur, values, errors, touched } = formik;

    return (
        <Box m="2px">
            <Header
                title="ROLES"
                subtitle="Manage Your Roles Here"
            />
            <Box display="flex" justifyContent="flex-end" mb={2}>
                <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleClickOpen}>
                    Add Role
                </Button>
            </Box>

            <Dialog open={open} onClose={handleClose}>
                <DialogTitle>{update ? 'Edit Role' : 'Add Role'}</DialogTitle>
                <form onSubmit={handleSubmit}>
                    <DialogContent>
                        <TextField
                            margin="dense"
                            id="roleName"
                            name="roleName"
                            label="Enter Role Name"
                            type="text"
                            fullWidth
                            variant="filled"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.roleName}
                            error={touched.roleName && Boolean(errors.roleName)}
                            helperText={touched.roleName && errors.roleName}
                            InputLabelProps={{
                                style: {
                                    color: theme.palette.text.primary
                                }
                            }}
                        />
                        <TextField
                            margin="dense"
                            id="description"
                            name="description"
                            label="Enter Role Description"
                            type="text"
                            fullWidth
                            variant="filled"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.description}
                            error={touched.description && Boolean(errors.description)}
                            helperText={touched.description && errors.description}
                            InputLabelProps={{
                                style: {
                                    color: theme.palette.text.primary
                                }
                            }}
                        />
                        {SideBar.filter(permission => !['Dashboard', 'Log Out'].includes(permission.categoryName)).map((permission) => (
                            <FormControlLabel
                                key={permission._id}
                                control={
                                    <Checkbox
                                        checked={values.permissions.includes(permission.categoryName)}
                                        onChange={(e) => {
                                            let newPermissions = values.permissions.filter(p => p !== 'Dashboard' && p !== 'Log Out' && p !== 'SlideBar Category');
                                            if (e.target.checked) {
                                                newPermissions.push(permission.categoryName);
                                            } else {
                                                newPermissions = newPermissions.filter(p => p !== permission.categoryName);
                                            }
                                            newPermissions = ['Dashboard', 'SlideBar Category', ...new Set(newPermissions), 'Log Out'];
                                            formik.setFieldValue('permissions', newPermissions);
                                        }}
                                        name="permissions"
                                        value={permission.categoryName}
                                    />
                                }
                                label={permission.categoryName}
                            />
                        ))}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleClose} style={{ color: theme.palette.text.primary }}>Cancel</Button>
                        <Button type="submit" style={{ color: theme.palette.text.primary }}>{update ? 'Update' : 'Add'}</Button>
                    </DialogActions>
                </form>
            </Dialog>

            <Box
                height="75vh"
                sx={useMemo(() => ({
                    "& .MuiDataGrid-root": {
                        border: "none",
                    },
                    "& .MuiDataGrid-columnHeaders": {
                        backgroundColor: colors.blueAccent[700],
                        borderBottom: "none",
                    },
                    "& .MuiDataGrid-virtualScroller": {
                        backgroundColor: colors.primary[400],
                    },
                    "& .MuiDataGrid-footerContainer": {
                        borderTop: "none",
                        backgroundColor: colors.blueAccent[700],
                    },
                    "& .MuiCheckbox-root": {
                        color: `${colors.greenAccent[200]} !important`,
                    },
                    "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
                        color: `${colors.grey[100]} !important`,
                        fontSize: "13px !important",
                    },

                }), [colors])}
            >
                <RoleDataGrid data={roles} columns={columns} />
            </Box>


            <Dialog
                fullScreen={fullScreen}
                open={openView}
                onClose={handleCloseView}
                aria-labelledby="responsive-dialog-title"
                maxWidth={"sm"}
                fullWidth
            >
                <DialogTitle id="responsive-dialog-title" style={{ backgroundColor: theme.palette.background.purple, textAlign: "center", fontWeight: 'bold', fontSize: '18px' }}>
                    {"Role Details"}
                </DialogTitle>
                <DialogContent style={{ backgroundColor: theme.palette.background.purple }}>
                    <DialogContentText>
                        <div>
                            <div>
                                <div>
                                    <p style={{
                                        backgroundColor: theme.palette.background.light,
                                        marginBottom: '2px',
                                        padding: '10px'
                                    }}>
                                        <strong>Name : </strong>
                                        <span style={{ wordBreak: 'break-word' }}>{viewData?.roleName}</span>
                                    </p>
                                    <p style={{
                                        backgroundColor: theme.palette.background.light,
                                        marginBottom: '2px',
                                        padding: '10px'
                                    }}>
                                        <strong>Description : </strong>
                                        <span style={{ wordBreak: 'break-word' }}>{viewData?.description}</span>
                                    </p>
                                    <p style={{
                                        backgroundColor: theme.palette.background.light,
                                        marginBottom: '2px',
                                        padding: '10px'
                                    }}>
                                        <strong>Permissions : </strong>
                                        <span style={{ wordBreak: 'break-word' }}>{viewData?.permissions.join(' , ')}</span>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </DialogContentText>
                </DialogContent>
                <DialogActions style={{ backgroundColor: theme.palette.background.purple }}>
                    <Button autoFocus onClick={handleCloseView} style={{ color: theme.palette.text.primary }}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={deleteConfirmOpen} onClose={cancelDelete}>
                <DialogContent>
                    Are you sure you want to delete this role?
                </DialogContent>
                <DialogActions>
                    <Button onClick={cancelDelete} style={{ color: theme.palette.text.primary }}>No</Button>
                    <Button onClick={confirmDelete} style={{ color: theme.palette.text.primary }}>Yes</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Role;
